import { Dispatch } from 'redux';

import { changeLanguage } from 'src/config/i18n';
import { ResolutionString } from 'src/lib/charting_library/charting_library';
import {
    MarginTransferable,
    TransferBalance,
    WalletTransferable,
    MarkNotificationAsReadRequestDTO,
} from 'src/types';
import { SetSettingsRequestDTO } from 'src/types/commonConfig';

import * as requestFromServer from './globalsCrud';
import { globalSlice, callTypes, GlobalState } from './globalsSlice';

const { actions } = globalSlice;

export const fetchIndexConfig = () => async (dispatch: Dispatch) => {
    try {
        const response = await requestFromServer.fetchIndexConfig();
        dispatch(actions.saveIndexConfig(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

// Important Note: Don't change callback value from queyParams until 25 line TODO's related to configV2JS resolved
export const fetchConfigV2 =
    (
        queryParams = 'custom_keys=loginReg,analytics&callback=JSON.stringify&tab=exchange&type=all&platform=1&without_country=true',
    ) =>
    async (dispatch: Dispatch) => {
        try {
            const response = await requestFromServer.fetchConfigV2(queryParams);
            let { data } = response; // TODO:: Needs to remove these lines once configV2JS api response fixed from backend
            data = data.split('JSON.stringify(')[1];
            data = JSON.parse(data.substr(0, data.length - 1));
            dispatch(actions.saveConfigV2(data));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const fetchTokenRates = (payload: object) => async (dispatch: Dispatch) => {
    try {
        const response = await requestFromServer.fetchTokenRates(payload);
        const { data } = response.data;
        dispatch(actions.saveTokenRates(data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const updateThemeData = (themePropObj: object) => (dispatch: Dispatch) => {
    dispatch(actions.updateThemeData(themePropObj));
};

export const updateHiddenAsset = (isAssetsHidden: boolean) => (dispatch: Dispatch) => {
    dispatch(actions.updateHiddenAsset(isAssetsHidden));
};

export const fetchWalletSubAccounts = () => async (dispatch: Dispatch) => {
    try {
        const response = await requestFromServer.fetchWalletSubAccounts();
        dispatch(actions.walletSubAccountsFetched(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const fetchTransferAvailable = (body: WalletTransferable) => async (dispatch: Dispatch) => {
    try {
        dispatch(
            actions.transferAvailableFetched({
                amount: 0,
            }),
        );
        const response = await requestFromServer.fetchTransferAvailable(body);
        dispatch(actions.transferAvailableFetched(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const fetchTransferAvailableMargin =
    (body: MarginTransferable) => async (dispatch: Dispatch) => {
        try {
            dispatch(
                actions.transferAvailableMarginFetched({
                    availWithdrawAmount: 0,
                }),
            );
            const response = await requestFromServer.fetchTransferAvailableMargin(body);
            dispatch(actions.transferAvailableMarginFetched(response.data));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const transferWalletBalance = (body: TransferBalance) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        await requestFromServer.transferWalletBalance(body);
        dispatch(actions.callEnded(callTypes.action));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const fetchCoinTokens = () => async (dispatch: Dispatch) => {
    try {
        const response = await requestFromServer.fetchCoinTokens();
        dispatch(actions.coinTokensFetched(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const updateFavoriteChartTypes =
    (body: { type: keyof GlobalState; chartType: string }) => async (dispatch: Dispatch) => {
        try {
            dispatch(actions.updateFavoriteChartTypes(body));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const updateChartType =
    (body: { type: keyof GlobalState; chartId: number }) => async (dispatch: Dispatch) => {
        try {
            dispatch(actions.updateChartType(body));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const updateChartInterval =
    (body: { type: keyof GlobalState; interval: string }) => (dispatch: Dispatch) => {
        dispatch(actions.updateChartInterval(body));
    };

export const updateFavoriteIntervals =
    (body: { type: keyof GlobalState; resolutions: ResolutionString[] }) =>
    (dispatch: Dispatch) => {
        dispatch(actions.updateFavoriteIntervals(body));
    };

export const getSettings = () => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));

    try {
        const response: any = await requestFromServer.getSettings();
        const res = response?.data?.commonConfig ? JSON.parse(response.data.commonConfig) : {};
        dispatch(actions.saveSettings(res));
        dispatch(actions.callEnded(callTypes.action));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const setSettings = (data: SetSettingsRequestDTO) => async (dispatch: Dispatch) => {
    try {
        await requestFromServer.setSettings(data);
        dispatch(actions.saveSettings(JSON.parse(data.common_config)));
        return data;
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const toggleRtl = (value: boolean) => async (dispatch: Dispatch) => {
    try {
        dispatch(actions.toggleRtl(value));
        localStorage.setItem('rtl', String(value));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const setActiveCurrency = (value: string) => async (dispatch: Dispatch) => {
    try {
        dispatch(actions.setActiveCurrency(value));
        localStorage.setItem('activeCurrency', value);
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const setActiveLanguage = (lang: string) => async (dispatch: Dispatch) => {
    try {
        dispatch(actions.setActiveLanguage(lang));
        localStorage.setItem('activeLanguage', lang);
        await changeLanguage(lang);
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const refreshCommonConfigFromStorage = () => async (dispatch: Dispatch) => {
    try {
        const lang = localStorage.getItem('activeLanguage');
        const unit = localStorage.getItem('activeCurrency');

        if (lang) {
            dispatch(actions.setActiveLanguage(lang));
            await changeLanguage(lang);
        }

        if (unit) {
            dispatch(actions.setActiveCurrency(unit));
        }
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const getNotifications = () => async (dispatch: Dispatch) => {
    try {
        const data = { page: 1, size: 10 };
        const res = await requestFromServer.getNotifications(data);
        dispatch(actions.saveNotifications(res));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const getPopupNotifications = () => async (dispatch: Dispatch) => {
    try {
        const res = await requestFromServer.getPopupNotifications();
        dispatch(actions.savePopupNotifications(res));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const markNotificationAsRead =
    (data: MarkNotificationAsReadRequestDTO) => async (dispatch: Dispatch) => {
        try {
            await requestFromServer.markNotificationAsRead(data);
            dispatch(actions.markNotificationAsRead(data));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };
